import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Row, Col, Card, CardBody, Label, FormGroup, Button } from "reactstrap";
import axiosInstance from "Api";
import { ReactComponent as LocationIcon } from "../../../../../../assets/utils/images/svgs/maps-icon.svg";
import ScholenService from "Api/Instellingen/Scholen";
import { useApiMutation } from "services/useApiMutation";

const Address = ({ data }) => {

  const [addressValid, setAddressValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [apiError, setApiError] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [confirmedAddressText, setConfirmedAddressText] = useState("");
  const [validatedData, setValidatedData] = useState(null);
  const [disableAfterValidation, setDisableAfterValidation] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const { trigger: validateAddressMutation, isMutating: validating } =
    useApiMutation();

  const { trigger: saveAddressMutation, isMutating: saving } =
    useApiMutation();

  useEffect(() => {
    if (addressValid || addressConfirmed) return;

    const verified = data?.detailList?.bIsAddressVerified;

    if (verified === 1) {
      setAddressValid(true);
      setAddressConfirmed(true);
      setApiError("");
    }
    else if (verified === 0) {
      setAddressValid(false);
      setAddressConfirmed(false);
      setApiError("Adres niet geverifieerd");
    }
  }, [data?.detailList?.bIsAddressVerified]);

  const validationSchema = Yup.object().shape({
    tAddress: Yup.string().required('Adres is verplicht'),
    vPostCode: Yup.string().required('Postcode is verplicht'),
    vCity: Yup.string().required('Plaats is verplicht'),

  });


  const handleAddressChange = () => {
    setAddressValid(false);
    setAddressConfirmed(false);
    setValidationError('');
    setApiError('');
    setConfirmedAddressText(null);
  };


  const confirmAddress = useCallback(async (vals, formik) => {
    try {
      const payload = {

        vVisitoraddress: validatedData.tAddress,
        vVisitpostcode: validatedData.vPostcode,
        vVisitcity: validatedData.vCity,
        iSchoolId: vals.iSchoolId,

      };

      const response = await saveAddressMutation({
        url: "/school-address/save",
        method: "POST",
        data: payload,
      });

      if (response.status === true) {
        setAddressConfirmed(true);
        setAddressValid(true);
        setConfirmedAddressText(response.data);
        setDisableAfterValidation(true);
        formik.setFieldValue("addressDisabled", true);
      } else {
        setApiError("Adres kon niet worden bevestigd.");
      }
    } catch {
      setApiError("Adresbevestiging mislukt.");
    }
  }, [validatedData, data.id]);

  const cities = [
    { key: "", value: "--- Kies land ---" },
    { key: "NL", value: "Nederland" },
    { key: "BE", value: "België" },
    { key: "DE", value: "Duitsland" }
  ];

  return (
    <Card className="main-card ">
      <CardBody className="d-flex flex-column">
        <h5 className="mb-4">Adresgegevens</h5>

        <Formik
          enableReinitialize
          initialValues={{
            tAddress: data?.detailList?.vVisitoraddress ?? '',
            vPostCode: data?.detailList?.vVisitpostcode ?? '',
            vCity: data?.detailList?.vVisitcity ?? '',
            iSchoolId: data?.detailList?.iSchoolId ?? data?.iSchoolId ?? '',
          }}
          validationSchema={validationSchema}
          onSubmit={() => { }}
        >
          {(formik) => (
            <Form>
              <FormGroup className="form-element"             >
                <Label for="tAddress">Adres</Label>
                <div className="w-100">
                  <Field
                    name="tAddress"
                    type="text"
                    className={`form-control ${formik.errors.tAddress && formik.touched.tAddress ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                  />
                  <ErrorMessage name="tAddress" component="small" className="invalid-feedback" />
                </div>
              </FormGroup>


              <FormGroup className="form-element">
                <Label>Postcode</Label>

                <div className="w-100">
                  <Field
                    name="vPostCode"
                    className={`form-control ${formik.errors.vPostCode && formik.touched.vPostCode ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                  />
                  <ErrorMessage name="vPostCode" component="small" className="invalid-feedback" />
                </div>
              </FormGroup>
              <FormGroup className="form-element">
                <Label>Woonplaats</Label>
                <div className="w-100">
                  <Field
                    name="vCity"
                    className={`form-control ${formik.errors.vCity && formik.touched.vCity ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                  />
                  <ErrorMessage name="vCity" component="small" className="invalid-feedback" />
                </div>

              </FormGroup>

              <Row >
                <Col sm={5}></Col>
                <Col sm={7}>

                  {confirmedAddressText?.formattedAddress && (
                    <p className=" fw-semibold small mb-2 mt-2 ">
                      Google Maps adres: {confirmedAddressText.formattedAddress}
                    </p>
                  )}


                  {!addressConfirmed && addressValid && (
                    <p className="small fw-semibold mt-1">
                      Adres gevalideerd voor automatische afstand berekening
                    </p>
                  )}

                  {validationError && (
                    <p className="text-danger small fw-semibold mt-1">{validationError}</p>
                  )}

                  {apiError && (
                    <p className="text-danger small fw-semibold mt-1">{apiError}</p>
                  )}


                  {!addressValid && (
                    <Button
                      outline
                      className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center mt-2"
                      type="button"
                      disabled={validating}
                      onClick={async () => {
                        const vals = formik.values;



                        if (!vals.tAddress || !vals.vPostCode || !vals.vCity) {
                          setValidationError("Vul alle adresvelden in.");
                          return;
                        }


                        setValidationError("");
                        setApiError("");
                        setConfirmedAddressText(null);
          

                        try {
                          const result = await validateAddressMutation({
                            url: "/school-address/validate",
                            method: "POST",
                            data: {
                              vVisitoraddress: vals.tAddress,
                              vVisitpostcode: vals.vPostCode,
                              vVisitcity: vals.vCity,
                              iSchoolId: vals.iSchoolId,
                            },
                          });

                          if (result.data.status) {
                            setAddressValid(true);
                            setConfirmedAddressText(result.data);
                            formik.setFieldValue("tAddress", result.data.tAddress);
                            formik.setFieldValue("vPostCode", result.data.vPostcode);
                            formik.setFieldValue("vCity", result.data.vCity);


                            setValidatedData(result.data);
                          } else {
                            setApiError('Google adres verificatie niet mogelijk, controleer het adres ');
                          }
                        } catch {
                          setApiError("Validatie mislukt.");
                        } 
                      }}
                    >
                      <LocationIcon style={{ width: 18, height: 18 }} />

                      Valideer adres

                    </Button>
                  )}


                  {addressValid && !addressConfirmed && (
                    <Button
                      outline
                     disabled={saving || isButtonDisabled}
                      className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center mt-2"
                      type="button"
                      onClick={async () => {
                        setIsButtonDisabled(true);

                        try {
                          await confirmAddress(formik.values, formik);
                        } finally {
                          setIsButtonDisabled(false);
                        }
                      }}
                    >
                      Toepassen
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>

  );
};

export default Address;
